import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogComponent } from '@progress/kendo-angular-dialog';

import { Communication } from '@ups/xplat/api/dto';
import { ReleaseNoteService } from '@ups/xplat/core';

import { sanitizeCommunicationHtml } from '../sanitize-communication-html.util';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ups-announcements-popup',
  templateUrl: './announcements-popup.component.html',
  styleUrls: ['./announcements-popup.component.scss'],
  standalone: false,
})
export class AnnouncementsPopupComponent implements OnInit {
  usedFeatureGroups: { sectionHeader: string; featureNotes: unknown[] }[];

  // WI 31712: signals, because the popup is opened from outside (showAnnouncements$ and the
  // manage-communications preview call showAnnouncementPopup()) and the download token lands in a
  // promise.
  readonly announcements = signal<Communication[]>([]);
  readonly showDialog = signal(false);
  readonly showDetails = signal(false);
  doLogging = true;
  openedAt = new Date().getTime();
  expandedAt: number = null; // Date().getTime() value
  closeSubscription: Subscription = null;
  readonly unseenAnnouncement = signal(false);
  readonly visibleIdx = signal(0);

  @ViewChild('dialog') dialog: DialogComponent;
  readonly helpDownloadToken = signal<string | undefined>(undefined);

  /** The announcement on screen, its sanitized body and its document links, for the template. */
  readonly current = computed(() => this.announcements()[this.visibleIdx()]);
  readonly safeShortDescription = computed(() =>
    this.sanitizeHtml(this.current()?.shortDescription)
  );
  readonly documentLinks = computed(() =>
    (this.current()?.documents ?? []).map((file) => ({
      file,
      url: this.releaseService.getDownloadUrl(
        file.location,
        file.fileName,
        this.helpDownloadToken(),
        true
      ),
    }))
  );

  constructor(
    public releaseService: ReleaseNoteService,
    public domSanitizer: DomSanitizer
  ) {
    this.releaseService.showAnnouncements$.subscribe((x) => {
      this.showAnnouncementPopup(x, true);
    });
  }

  ngOnInit() {}

  /**
   * WI 31640: sanitize an admin-authored announcement body for [innerHTML].
   * Replaces the former bypassSecurityTrustHtml, which skipped sanitization and
   * let a stored script/handler execute for every viewer.
   */
  sanitizeHtml(value: string | null | undefined): string {
    return sanitizeCommunicationHtml(this.domSanitizer, value);
  }

  onCloseAnnouncement() {
    if (this.doLogging) {
      const now = new Date().getTime();
      if (this.announcements().length) {
        this.releaseService.uploadLogs({
          releaseNoteId: this.current().communicationId,
          minutesAfterExpansion:
            this.expandedAt == null ? 0 : (now - this.expandedAt) / 60000,
          minutesSpent: (now - this.openedAt) / 60000,
        });
      }
    }
  }

  onClose() {
    this.onCloseAnnouncement();
    this.showDialog.set(false);
  }

  toggleDetails() {
    this.showDetails.update((shown) => !shown);
    this.expandedAt = this.expandedAt || new Date().getTime(); // if expandedAt not null, new date.
  }

  public showAnnouncementPopup(comms: Communication[], logging = true) {
    this.announcements.set(comms);
    // Start at the first item: a reopen after paging forward left visibleIdx past the end.
    this.visibleIdx.set(0);
    this.usedFeatureGroups = [];
    this.showDetails.set(false);
    this.doLogging = logging;

    this.releaseService.getDownloadToken().then((x) => {
      this.helpDownloadToken.set(x);
    });

    this.showDialog.set(true);
    comms[0].viewed = true;
    this.initializeData();
  }

  initializeData() {
    this.expandedAt = null;
    this.openedAt = new Date().getTime();
    const comms = this.announcements();
    comms[this.visibleIdx()].viewed = true;
    this.unseenAnnouncement.set(comms.some((x) => x.viewed == false));
  }

  previousAnnouncement() {
    this.onCloseAnnouncement();
    this.initializeData();
    this.visibleIdx.update((idx) => idx - 1);
  }

  nextAnnouncement() {
    this.onCloseAnnouncement();
    this.initializeData();
    this.visibleIdx.update((idx) => idx + 1);
  }
}
